import { Suspense, useContext, useState, useEffect, useRef } from 'react'
import Section from './_Section'
import ButtonEl from '../helpers/ctas/Button'
import Loader from '../helpers/Loader'
import themeConfig from '../../../theme.config'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import { LockClosedIcon, MapPinIcon } from '@heroicons/react/24/solid'
import { transformPaddingToTailwind } from '../../helpers/style'
import { dataLayerPush } from '../../helpers/thirdparty'
import { MarkupContext } from '../../providers/Markup'
import SearchFilters from './SearchFilters'
import MetaBadge from '../helpers/MetaBadge'
import { CalendarIcon } from '@heroicons/react/24/outline'
import { formatDate, formatEventDate } from '../../helpers/util'
import { smoothScrollToElement } from '../../helpers/smoothScroll'
import ReactPaginate from 'react-paginate'

// The results list paginates client-side, so a single request must load the whole result set
// rather than one API page. 1000 is Algolia's max hitsPerPage (and its default paginationLimitedTo),
// applied per index — so a multi-index/global search returns up to 1000 hits from each index.
const RESULTS_FETCH_SIZE = 1000

// Listing pages whose category strip (the blue tabs at the top) is intentionally NOT rendered on the
// frontend, even though the backend still returns a `categories` facet for them. Product decision
// (NDS-272): Training and Resource Hub listings show no tabs. Backend left unchanged.
const TAB_SUPPRESSED_CONTENT_TYPES = ['training', 'training-development', 'training-and-development', 'resource-hub', 'news']

async function getResults(searchTerm, filters = {}) {
  try {
    const params = new URLSearchParams()
    if (searchTerm) params.set('q', searchTerm)
    params.set('pageSize', String(RESULTS_FETCH_SIZE))

    if (filters.contentType) params.set('index', filters.contentType)

    // One clause per facet, values comma-joined: "topic:AI,News;location:VIC". The backend
    // parses each `;` group into a single key → multiple values, so values for one facet must
    // share a clause (separate `topic:` groups would collide and only the last would survive).
    const filterParts = []
    if (filters.topic?.length) filterParts.push(`topic:${filters.topic.join(',')}`)
    if (filters.location?.length) filterParts.push(`location:${filters.location.join(',')}`)
    if (filters.audience?.length) filterParts.push(`audience:${filters.audience.join(',')}`)
    if (filters.trainingFormat) filterParts.push(`trainingFormat:${filters.trainingFormat}`)

    if (filterParts.length > 0) params.set('filters', filterParts.join(';'))
    if (filters.dateFrom) params.set('dateFrom', filters.dateFrom)
    if (filters.dateTo) params.set('dateTo', filters.dateTo)

    const response = await fetch(`${window.location.origin}/api/search?${params.toString()}`)
    const responseJson = await response.json()

    if (responseJson?.success && responseJson.result) {
      return {
        success: true,
        result:
          responseJson.result.results?.map((r) => ({
            title: r.title,
            nodeName: r.title,
            url: r.url,
            description: r.description,
            highlightedDescription: r.highlightedDescription,
            category: r.category,
            publicationDate: r.publicationDate,
            readTime: r.readTime,
            deliveryMode: r.deliveryMode,
            tags: r.tags,
            topic: r.topic,
            eventType: r.eventType,
            resourceType: r.resourceType,
            isOnDemand: r.isOnDemand,
            isLocked: r.isLocked,
            location: r.location,
            eventDate: r.eventDate,
            eventDateDisplay: r.eventDateDisplay,
            memberFee: r.memberFee,
            nonMemberFee: r.nonMemberFee,
          })) || [],
        totalResults: responseJson.result.totalResults,
        facets: responseJson.result.facets,
        isFallbackActive: responseJson.result.isFallbackActive || false,
      }
    }
    return responseJson
  } catch (error) {
    console.error(error)
  }
}

const ResultsWrapper = (props) => {
  const [results, setResults] = useState(null)
  const [resultsText, setResultsText] = useState('Found {x} results for {keyword}')
  const [searchTerm, setSearchTerm] = useState('')
  const [totalPages, setTotalPages] = useState(1)
  const [itemOffset, setItemOffset] = useState(0)

  const pageSize = props.pageSize || 10

  // `props.searchTerm` is the URL `q` value. Every search form double-encodes it (encodeURIComponent
  // then URLSearchParams serialisation), and URLSearchParams.get() strips only one layer — so the
  // prop is still percent-encoded once and a space arrives as "%20". It MUST be decoded before
  // hitting the API: Algolia tokenises the literal "%20" into a spurious "20" term AND-ed with the
  // real words, so a multi-word query matches nothing (NDS-256). Display sites
  // already decode (see the `decodeURIComponent(props.searchTerm)` calls below) — the API call must too.
  const apiSearchTerm = props.searchTerm ? decodeURIComponent(props.searchTerm) : ''

  // CMS "Pagination text" is an ordered pair [Previous, Next]. The design keeps arrow-only
  // visuals, so these drive the screen-reader text / aria-labels on the prev/next controls. (NDS-150)
  const paginationText = props.paginationText || []
  const previousLabelText = paginationText[0] || 'Previous'
  const nextLabelText = paginationText[1] || 'Next'

  const [activeTab, setActiveTab] = useState('All')
  const [tabResults, setTabResults] = useState(null)
  const [tabLoading, setTabLoading] = useState(false)
  const isInitialLoad = useRef(true)
  const paginationNavRef = useRef(null)

  // A tab maps to a server-side facet filter: Resource Hub → topic, Training → trainingFormat.
  // Clicking a tab re-queries the API with that filter (exactly like the sidebar filters), so
  // every matching record is returned — not just the ones whose first topic happened to match.
  const categoryFacetField = ['training', 'training-development', 'training-and-development'].includes(
    props.filters.contentType,
  )
    ? 'trainingFormat'
    : 'topic'

  const handleTabClick = (tabValue) => {
    setActiveTab(tabValue)
    if (tabValue === 'All') {
      setTabResults(null)
      return
    }
    const tabFilters = {
      ...props.filters,
      [categoryFacetField]: categoryFacetField === 'topic' ? [tabValue] : tabValue,
    }
    setTabLoading(true)
    getResults(apiSearchTerm, tabFilters)
      .then((response) => {
        if (!response?.errorMessage) setTabResults(response)
      })
      .finally(() => setTabLoading(false))
  }

  // "All" shows the unfiltered base results; a category tab shows the server-filtered set fetched
  // in handleTabClick. The tab strip itself always derives from the base results' facets below, so
  // selecting a tab never collapses the strip.
  const activeResults = activeTab === 'All' ? results : tabResults
  const filteredResults = activeResults?.result || []

  const endOffset = itemOffset + pageSize
  const currentItems = filteredResults.slice(itemOffset, endOffset)

  useEffect(() => {
    if (pageSize > 0) {
      const size = Math.ceil(filteredResults.length / pageSize)
      setTotalPages(() => (!isNaN(size) ? size : 0))
    }
  }, [filteredResults.length, pageSize])

  useEffect(() => {
    setItemOffset(0)
  }, [activeTab])

  useEffect(() => {
    const hasSearch = props.searchTerm
    const hasFilters = Object.keys(props.filters).length > 0

    if (hasSearch || hasFilters) {
      if (hasSearch) setSearchTerm(decodeURIComponent(props.searchTerm))

      const fetchResults = getResults(apiSearchTerm, props.filters || {})
      fetchResults.then((response) => {
        if (!response?.errorMessage) {
          setResults(response)
          setActiveTab('All')
          setTabResults(null)

          if (isInitialLoad.current && response?.success) {
            // trigger the view results regardless of the number of results
            dataLayerPush({
              event: 'search_result_viewed',
              search_term: (hasSearch ? decodeURIComponent(props.searchTerm) : searchTerm) || undefined,
              quantity: String(response.result.length),
            })
          }
        }
        if (isInitialLoad.current) {
          isInitialLoad.current = false
        }
      })

      setResultsText((prevState) => props.searchResultText || prevState)
    } else {
      setSearchTerm('')
    }
  }, [props.searchTerm, props.filters])

  const submitForm = (evt) => {
    evt.preventDefault()
    const inputValue = evt.target.querySelector('#in-page-search')?.value?.trim() || ''

    if (inputValue) {
      dataLayerPush({
        event: 'onsite_search',
        search_term: inputValue,
      })
    }

    const searchUrl = new URL(window.location.href)
    if (inputValue) {
      searchUrl.searchParams.set('q', encodeURIComponent(inputValue))
    } else {
      searchUrl.searchParams.delete('q')
    }

    window.location = searchUrl.toString()
  }

  const handlePageClick = (event) => {
    const newOffset = (event.selected * pageSize) % filteredResults.length
    setItemOffset(newOffset)
    requestAnimationFrame(() => {
      smoothScrollToElement(paginationNavRef.current?.parentElement)
    })
  }

  const handleApplyFilters = (newFilters) => {
    props.setFilters((prev) => ({ ...prev, ...newFilters }))

    const keyToParam = (key) => (key === 'contentType' ? 'content-type' : key)
    const searchUrl = new URL(window.location.href)

    Object.keys(newFilters).forEach((key) => {
      const paramKey = keyToParam(key)
      searchUrl.searchParams.delete(paramKey)
      if (Array.isArray(newFilters[key]) && newFilters[key].length > 0) {
        searchUrl.searchParams.set(paramKey, newFilters[key].join(','))
      } else if (!Array.isArray(newFilters[key]) && newFilters[key]) {
        searchUrl.searchParams.set(paramKey, newFilters[key])
      }
    })

    // URLSearchParams encodes commas as %2C — restore them to literal commas
    window.history.pushState({}, '', searchUrl.toString().replace(/%2C/gi, ','))
  }

  const handleClearFilters = () => {
    const clearedFilters = { topic: [], audience: [], location: [], dateFrom: '', dateTo: '' }
    props.setFilters((prev) => ({ ...prev, ...clearedFilters }))

    const searchUrl = new URL(window.location.href)
    ;['topic', 'audience', 'location', 'dateFrom', 'dateTo'].forEach((key) => searchUrl.searchParams.delete(key))
    window.history.pushState({}, '', searchUrl)
  }

  const categories = results?.facets?.categories || []
  const tabsSuppressed = TAB_SUPPRESSED_CONTENT_TYPES.includes((props.filters.contentType || '').toLowerCase())
  const hasTabs = categories.length > 0 && !results?.isFallbackActive && !tabsSuppressed

  return (
    <div className="pt-10 lg:pt-16">
      {hasTabs && (
        <div
          className="flex overflow-x-auto bg-(--sb-secondary-light-tint-blue) mb-8 w-full"
          role="tablist"
          aria-label="Filter results by category"
        >
          <button
            role="tab"
            aria-selected={activeTab === 'All'}
            onClick={() => handleTabClick('All')}
            className={`shrink-0 whitespace-nowrap px-5 py-3 text-base transition-colors border-b-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 ${
              activeTab === 'All'
                ? 'border-(--sb-primary-bg) font-bold text-(--color-secondary-text)!'
                : 'border-transparent font-normal text-(--sb-common-base-body-text-color)! hover:border-gray-300'
            }`}
          >
            All
          </button>
          {categories.map((cat, idx) => (
            <button
              key={idx}
              role="tab"
              aria-selected={activeTab === cat.value}
              onClick={() => handleTabClick(cat.value)}
              className={`shrink-0 whitespace-nowrap px-5 py-3 text-base transition-colors border-b-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 ${
                activeTab === cat.value
                  ? 'border-(--sb-primary-bg) font-bold text-(--color-secondary-text)!'
                  : 'border-transparent font-normal text-(--sb-common-base-body-text-color)! hover:border-gray-300'
              }`}
            >
              {cat.value} ({cat.count})
            </button>
          ))}
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-[41px] w-full items-start">
        {/* Sidebar Filters */}
        <div className="w-full lg:w-[320px] shrink-0">
          <SearchFilters
            facets={results?.facets || {}}
            appliedFilters={props.filters}
            onApplyFilters={handleApplyFilters}
            onClearFilters={handleClearFilters}
            category={props.sectionTitle}
          />
        </div>

        {/* Main Results Container */}
        <div className="flex-1 min-w-0 w-full">
          <div className="w-full my-4 md:mb-8 mt-0">
            <form role="search" className="grid grid-cols-[1fr_auto] gap-4 items-center" onSubmit={submitForm}>
              {/* WCAG: Added accessible label for screen readers */}
              <label htmlFor="in-page-search" className="sr-only">
                {props.placeholderText || 'Search site'}
              </label>
              <input
                id="in-page-search"
                type="text"
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={props.placeholderText}
                autoComplete="off"
                className="w-full h-12 rounded-md border-(--sb-search-input-border) px-5 min-w-0 text-(--color-black) shadow-xs shadow-black/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600"
                value={searchTerm}
              />
              <ButtonEl theme="primary" outerClass="h-12 w-auto whitespace-nowrap" type="submit">
                {props.searchButtonText}
              </ButtonEl>
            </form>
          </div>

          {tabLoading ? (
            <Loader />
          ) : (
            <>
          {/* WCAG: aria-live ensures screen readers announce when results are updated via filters */}
          <div aria-live="polite" aria-atomic="true">
            {resultsText && (
              <>
                {results && !results.success && (
                  <p className="text-xl md:text-2xl mb-8 font-normal text-(--color-primary-text)">
                    {resultsText
                      .replace('{x}', 0)
                      .replace('{keyword}', props.searchTerm ? `"${decodeURIComponent(props.searchTerm)}"` : '""')}
                  </p>
                )}
                {results && results.success && (
                  <p className="text-xl md:text-2xl mb-8 font-normal text-(--color-primary-text)">
                    {resultsText
                      .replace('{x}', filteredResults.length || 0)
                      .replace('{keyword}', props.searchTerm ? `"${decodeURIComponent(props.searchTerm)}"` : '""')}
                  </p>
                )}
              </>
            )}
          </div>

          {results && <hr className="w-full mt-8 border-t border-(--color-border-tertiary)" />}

          {results?.isFallbackActive && (
            <div className="bg-amber-50 border-l-4 border-amber-500 text-amber-700 p-4 mb-8" role="alert">
              <p className="font-bold">Notice</p>
              <p>Showing basic results. Advanced filters and categories are temporarily unavailable.</p>
            </div>
          )}

          <ul>
            {currentItems?.map((res, idx) => {
              const isLast = idx === currentItems.length - 1
              return <ResultsItem key={`result-${idx}`} result={res} idx={idx} isLast={isLast} searchTerm={searchTerm} quantity={filteredResults.length} />
            })}
          </ul>

          {/* Editor-configurable empty state shown once a query/filter has returned no results (NDS-153) */}
          {results && filteredResults.length === 0 && (
            <p className="text-lg font-normal text-(--sb-common-base-body-text-color) py-8">
              {props.noResultsText || 'No results found. Please try a different search term.'}
            </p>
          )}

          {/* Pagination Box */}
          {filteredResults.length > 0 && (
            <nav
              ref={paginationNavRef}
              aria-label="Pagination"
              // Changed max-lg:items-start to max-lg:items-center, added w-full
              className="flex max-lg:flex-col max-lg:items-center max-lg:gap-y-6 justify-between items-center sm:px-0 w-full pt-6 mt-8"
            >
              <span
                // Added text-center w-full lg:w-auto to guarantee centering on mobile
                className="text-(--sb-pagination-result-text) text-lg font-normal text-center w-full lg:w-auto"
                aria-live="polite"
              >
                Showing {currentItems.length} out of {filteredResults.length} results
              </span>

              {totalPages > 1 && (
                <ReactPaginate
                  breakLabel="..."
                  previousLabel={
                    <span className="flex items-center justify-center">
                      <span className="sr-only">{previousLabelText}</span>
                      <ChevronLeftIcon role="presentation" className="h-5 w-5" aria-hidden="true" />
                    </span>
                  }
                  nextLabel={
                    <span className="flex items-center justify-center">
                      <span className="sr-only">{nextLabelText}</span>
                      <ChevronRightIcon role="presentation" className="h-5 w-5" aria-hidden="true" />
                    </span>
                  }
                  onPageChange={handlePageClick}
                  pageRangeDisplayed={3}
                  marginPagesDisplayed={1}
                  pageCount={totalPages}
                  forcePage={Math.floor(itemOffset / pageSize)}
                  renderOnZeroPageCount={null}
                  hrefBuilder={(pageIndex) => `?page=${pageIndex + 1}`}
                  hrefAllControls={true}
                  ariaLabelBuilder={(pageIndex) => `Page ${pageIndex}`}
                  containerClassName="flex flex-wrap items-center justify-center gap-2 max-w-full border-none border-0 shadow-none ring-0 outline-none"
                  // --- Forces the <li> wrappers to display, overriding any hidden mobile CSS
                  pageClassName="!block"
                  breakClassName="!block"
                  previousClassName="!block"
                  nextClassName="!block"
                  // --------------------------------------------------------------------------------------

                  pageLinkClassName="w-10 h-10 flex items-center justify-center text-sm transition-colors text-(--color-primary-text) font-medium border border-(--sb-pagination-button-border) rounded-md hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600"
                  activeLinkClassName="bg-(--sb-pagination-current-bg) text-white! border-(--sb-pagination-current-border) hover:bg-(--sb-pagination-current-bg)!"
                  previousLinkClassName="w-10 h-10 flex items-center justify-center text-sm transition-colors text-(--color-primary-text) font-medium border border-(--sb-pagination-button-border) rounded-md hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600"
                  nextLinkClassName="w-10 h-10 flex items-center justify-center text-sm transition-colors text-(--color-primary-text) font-medium border border-(--sb-pagination-button-border) rounded-md hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600"
                  disabledClassName="opacity-50"
                  disabledLinkClassName="hover:bg-transparent cursor-not-allowed outline-none focus-visible:ring-0"
                  breakLinkClassName="w-10 h-10 flex items-center justify-center text-sm font-medium border border-(--color-border-secondary) rounded-md text-(--color-primary-text)"
                />
              )}
            </nav>
          )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

const ResultsItem = (props) => {
  const { createMarkup, detectPaletteClass } = useContext(MarkupContext)
  const [onPage, setOnPage] = useState(false)

  useEffect(() => {
    setOnPage(() => {
      return props.idx >= props.startIndex && props.idx <= props.endIndex
    })
  }, [props.startIndex, props.endIndex, props.idx])

  const getUrlPath = (url) => {
    if (!url) return ''
    try {
      const urlObj = new URL(url, window.location.origin)
      return urlObj.pathname
    } catch {
      return url
    }
  }

  // date display condition
  const dateDisplay =
    props.result?.resourceType === 'article'
      ? props.result?.publicationDate
        ? formatDate(props.result.publicationDate)
        : ''
      : undefined

  const isLocked = props.result?.isLocked ?? false

  // Prefer Algolia's <mark>-highlighted excerpt (NDS-151); fall back to the plain description.
  const description = props.result?.highlightedDescription || props.result?.description

  const handleCardClick = (e) => {
    dataLayerPush(
      {
        event: 'event_click',
        event_title: props.result?.title,
        event_date: formatEventDate(props.result?.publicationDate),
        event_location: props.result?.location ? String(props.result.location) : null,
        member_fee: props.result?.memberFee ?? null,
        nonmember_fee: props.result?.nonMemberFee ?? null,
        event_delivery: props.result?.deliveryMode ?? null,
        event_state: props.result?.tags?.[0] ?? null,
        event_type: props.result?.eventType ?? null,
      },
      e.currentTarget,
    )
    dataLayerPush({
      event: 'search_result_selected',
      category: 'Results',
      search_term: props.searchTerm || undefined,
      click_text: props.result?.title,
      quantity: String(props.quantity ?? 0),
    })
  }

  // Badge primary comes from topic[0]; tags also include location strings so we avoid them
  const badgePrimaryText = props.result?.topic?.[0] || ''
  const locationText = Array.isArray(props.result?.location)
    ? props.result.location.filter(Boolean).join(', ')
    : props.result?.location || ''
  const eventDateText =
    props.result?.eventDateDisplay ||
    (props.result?.eventDate ? formatDate(props.result.eventDate) : '')

  // conditionally render the read time / deliver mode
  const badgeSecondaryText =
    props.result?.resourceType === 'article'
      ? props.result?.readTime
        ? `${props.result.readTime} min`
        : ''
      : props.result?.deliveryMode

  // logic to get the badge color
  let badgeTagType
  switch (props.result?.eventType) {
    case 'upcoming-events':
      badgeTagType = 'event'
      break
    case 'training':
      badgeTagType = 'training'
      break
    default:
      badgeTagType = 'unlocked_news_articles'
      break
  }

  return (
    <>
      <li className={`py-8 border-b border-(--color-border-tertiary)`} data-idx={props.idx}>
        <div className="flex flex-col md:flex-row items-start md:items-center justify-start gap-3 md:gap-8 mb-8">
          {badgePrimaryText && (
            <div>
              <MetaBadge
                primaryText={badgePrimaryText}
                secondaryText={badgeSecondaryText}
                tagType={isLocked ? 'locked_news_articles' : badgeTagType}
                isLocked={isLocked}
                icon={isLocked ? <LockClosedIcon className="w-3.5 h-3.5" aria-hidden="true" /> : null}
              />
            </div>
          )}

          {locationText && (
            <div className="flex items-center gap-3">
              <MapPinIcon className="w-4 h-4 text-(--color-primary-text) shrink-0" aria-hidden="true" />
              <span className="text-(--sb-common-base-body-text-color) text-xs font-normal">{locationText}</span>
            </div>
          )}
          {eventDateText && (
            <div className="flex items-center gap-3">
              <CalendarIcon className="w-4 h-4 text-(--color-primary-text) shrink-0" aria-hidden="true" />
              <span className="text-(--sb-common-base-body-text-color) text-xs font-normal">{eventDateText}</span>
            </div>
          )}
        </div>
        {dateDisplay && (
          <div className="flex items-center gap-3 mb-8">
            <CalendarIcon className="w-4 h-4 text-(--color-primary-text) shrink-0" aria-hidden="true" />
            <span className="text-(--sb-common-base-body-text-color) text-xs font-normal">
              Published on {dateDisplay}
            </span>
          </div>
        )}
        <a
          href={props.result?.url}
          className="search-result-title-link inline-block mb-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 rounded-sm"
          onClick={handleCardClick}
        >
          <strong
            className={detectPaletteClass(
              'text-(--color-primary-text) text-2xl md:text-4xl font-light mb-0',
            )}
          >
            {props.result?.title ?? props.result?.nodeName}
          </strong>
        </a>
        {description ? (
          <p data-rte="true" dangerouslySetInnerHTML={createMarkup(description)}></p>
        ) : (
          props.result?.url && (
            <p className="text-sm md:text-base font-normal text-(--sb-common-base-body-text-color)! mt-2">
              {getUrlPath(props.result.url)}
            </p>
          )
        )}
      </li>
    </>
  )
}

const SearchResult = (props) => {
  const [searchTerm, setSearchTerm] = useState()
  const [filters, setFilters] = useState({})
  const componentPadding = transformPaddingToTailwind(props.componentPadding)

  useEffect(() => {
    const url = new URL(window.location)
    const search = new URLSearchParams(url.search)
    setSearchTerm(search.get('q'))

    // Read a multi-value facet from the URL, accepting both repeated params (?topic=AI&topic=News)
    // and the comma form (?topic=AI,News), or a mix of the two.
    const readMulti = (key) =>
      search
        .getAll(key)
        .flatMap((v) => v.split(','))
        .map((v) => v.trim())
        .filter(Boolean)

    setFilters({
      contentType: search.get('content-type') || '',
      topic: readMulti('topic'),
      audience: readMulti('audience'),
      location: readMulti('location'),
      trainingFormat: search.get('trainingFormat') || '',
      dateFrom: search.get('dateFrom') || '',
      dateTo: search.get('dateTo') || '',
    })
  }, [])

  return (
    <Section
      type="searchResult"
      outerClass={componentPadding}
      innerClass="container mx-auto w-full px-5 md:px-8 lg:px-12"
      {...props}
    >
      <Suspense key={`search-loader`} fallback={<Loader />}>
        <ResultsWrapper {...props} searchTerm={searchTerm} filters={filters} setFilters={setFilters} />
      </Suspense>
    </Section>
  )
}

export default SearchResult
